import { useState, useEffect } from "react";
import { Form, DatePicker, Select, Button, Tag, Checkbox, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useGetDealsConvertedQuery, useBulkUpdateCommissionMutation } from "@/redux/rtk/features/CRM/deal/dealApi";
import { useGetDealStagesQuery } from "@/redux/rtk/features/CRM/dealStage/dealStageApi";
import Card from "@/UI/Card";
import TableComponent from "@/components/CommonUi/TableComponent";
import moment from "moment";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadAllStaff, clearUserList } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent, clearAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";

const { Option } = Select;
const { RangePicker } = DatePicker;

export default function CommissionBulkUpdate({ dealId }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [selectedIds, setSelectedIds] = useState([]);
    const [pageConfig, setPageConfig] = useState({
        page: 1,
        count: 10,
        dealStage: null,
        startDate: null,
        endDate: null,
        agentCommissionPaid: null,
        dealOwner: null,
        companyId: null,
        ...(dealId && { id: dealId })
    });

    const dispatch = useDispatch();
    const { list: userList, loading: userLoading } = useSelector((state) => state.users);
    const { list: companyList, loading: companyLoading } = useSelector((state) => state.agent);

    useEffect(() => {
        dispatch(loadAllStaff({ query: "all" }));
        dispatch(loadAllAgent());
        return () => {
            dispatch(clearUserList());
            dispatch(clearAgent());
        };
    }, [dispatch]);

    const { data: stagesData, isLoading: stagesLoading } = useGetDealStagesQuery();
    const dealStages = Array.isArray(stagesData) ? stagesData : [];

    // Find "Win" stage ID by name (case-insensitive)
    useEffect(() => {
        if (dealStages.length > 0 && !pageConfig.dealStage) {
            const winStage = dealStages.find(s => s.stageName.toLowerCase() === 'win');
            if (winStage) {
                setPageConfig(prev => ({ ...prev, dealStage: winStage.id }));
                form.setFieldsValue({ dealStageId: winStage.id });
            }
        }
    }, [dealStages, pageConfig.dealStage, form]);

    const { data, isLoading } = useGetDealsConvertedQuery(pageConfig);
    const [bulkUpdate, { isLoading: isUpdating }] = useBulkUpdateCommissionMutation();

    const onFilter = (values) => {
        const { dates, dealStageId, agentCommissionPaid, dealOwner, companyId } = values;
        setPageConfig(prev => ({
            ...prev,
            page: 1,
            dealStage: dealStageId,
            startDate: dates ? dates[0].format("YYYY-MM-DD") : null,
            endDate: dates ? dates[1].format("YYYY-MM-DD") : null,
            agentCommissionPaid,
            companyId,
        }));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = data?.getAllDeal?.map(item => item.id) || [];
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(item => item !== id));
        }
    };

    const handleBulkUpdate = async (status) => {
        if (selectedIds.length === 0) return;
        await bulkUpdate({ ids: selectedIds, status }).unwrap();
        setSelectedIds([]);
    };

    const columns = [
        {
            title: (
                <Checkbox
                    onChange={handleSelectAll}
                    checked={selectedIds.length > 0 && selectedIds.length === data?.getAllDeal?.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < data?.getAllDeal?.length}
                />
            ),
            key: "selection",
            render: (item) => (
                <Checkbox
                    checked={selectedIds.includes(item.id)}
                    onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                />
            ),
        },
        {
            title: "#ID",
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("deal.dealName"),
            key: "dealName",
            render: (item) => <Link to={`/admin/deal/${item.id}`}>{item.dealName}</Link>,
        },
        {
            title: t("deal.lead"),
            key: "lead",
            render: (item) => item.lead ? `${item.lead.firstName} ${item.lead.lastName}` : "-",
        },
        {
            title: t("deal.dealOwner"),
            key: "dealOwner",
            render: (item) => item.dealOwner ? `${item.dealOwner.firstName} ${item.dealOwner.lastName}` : "-",
        },
        {
            title: t("company.companyName"),
            key: "company",
            render: (item) => item.contact?.company?.companyName || "-",
        },
        {
            title: t("deal.stage"),
            key: "stage",
            render: (item) => item.dealStage?.stageName || "-",
        },
        {
            title: t("common.date"),
            key: "createdAt",
            dataIndex: "createdAt",
            render: (date) => moment(date).format("YYYY-MM-DD"),
        },
        {
            title: t("deal.agentCommission"),
            key: "agentCommissionPaid",
            dataIndex: "agentCommissionPaid",
            render: (status) => (
                <Tag color={status === "yes" ? "green" : "red"}>
                    {status === "yes" ? t("common.yes").toUpperCase() : t("common.no").toUpperCase()}
                </Tag>
            ),
        },
    ];

    return (
        <Card className="m-4">
            <div className="p-4 border-b">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">{t("deal.bulkUpdateTitle")}</h2>

                {!dealId && (
                <Form form={form} layout="inline" onFinish={onFilter} className="mb-4">
                    <Form.Item name="dealStageId" label={t("deal.stage")}>
                        <Select
                            style={{ width: 170 }}
                            placeholder={t("deal.selectStage") || "Select Stage"}
                            allowClear
                            loading={stagesLoading}
                            options={dealStages.map(s => ({ label: s.stageName, value: s.id }))}
                        />
                    </Form.Item>

                    <Form.Item name="dates" label={t("deal.dateRange")}>
                        <RangePicker />
                    </Form.Item>

                    <Form.Item name="agentCommissionPaid" label={t("deal.agentCommission")}>
                        <Select style={{ width: 120 }} placeholder={t("deal.selectStatus") || "Status"} allowClear>
                            <Option value="no">{t("common.no").toUpperCase()}</Option>
                            <Option value="yes">{t("common.yes").toUpperCase()}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="companyId" label={t("company.title")}>
                        <Select
                            style={{ width: 150 }}
                            placeholder={t("deal.selectAgent") || "Select Agent"}
                            allowClear
                            loading={companyLoading}
                            options={companyList?.map(c => ({ label: c.companyName, value: c.id }))}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit">{t("common.filter")}</Button>
                    </Form.Item>
                </Form>
                )}

                <div className="flex justify-between items-center bg-blue-50 p-3 rounded mb-4">
                    <div>
                        {t("deal.selectedDeals", { count: selectedIds.length })}
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            className="bg-green-600 border-green-600"
                            disabled={selectedIds.length === 0}
                            loading={isUpdating}
                            onClick={() => handleBulkUpdate("yes")}
                        >
                            {t("deal.markAsYes")}
                        </Button>
                        <Button
                            danger
                            disabled={selectedIds.length === 0}
                            loading={isUpdating}
                            onClick={() => handleBulkUpdate("no")}
                        >
                            {t("deal.markAsNo")}
                        </Button>
                    </Space>
                </div>
            </div>

            <TableComponent
                list={data?.getAllDeal}
                columns={columns}
                loading={isLoading}
                total={data?.totalDeal}
                setPageConfig={setPageConfig}
                isSearch={false}
            />
        </Card>
    );
}
